import { GraphemeSvgGlyph } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.grapheme.glyph.svg";
import { Text } from "@-/dev.improve.haniverse.components.ui.text";
import type { ComponentButtonMorphemeSlice } from "@-/dev.improve.haniverse.lib.morpheme.entities";
import { useGetGraphemeGlyph } from "@-/dev.improve.haniverse.routes.nav.app._lib.morpheme.grapheme.glyph/client";
import { usePrevalentPinyin } from "@-/dev.improve.haniverse.routes.nav.app._lib.morpheme.grapheme.pinyin.prevalence";
import { toDiacriticPinyin } from "@-/dev.improve.haniverse.lib.morpheme.grapheme.pinyin";
import { MorphemeMnemonicsMeaningViewContext } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.panels._views.grapheme.mnemonics._components.panel-content.views.meaning._context";
import { useMorphemePanelsHrefNavigator } from "@-/dev.improve.haniverse.lib.morpheme.page.navigator";
import { useContext, useMemo } from "react";
import { View } from "@-/com.npmjs.react-native";
import { LongPressable } from "@-/dev.improve.haniverse.components.ui.long-pressable";
import { morphemeSelectionItem } from "@-/dev.improve.haniverse.routes.nav.app._context";
import { useAppSelection } from "@-/dev.improve.haniverse.routes.nav.app._lib.morpheme.tile.selection";
import { cn } from "@-/com.npmjs.cn";
import { getMnemonicGraphemeLabel } from "@-/dev.improve.haniverse.lib.morpheme.description";
import { GraphemeMorphemePanelsContext } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.panels._context";
import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";

export function DecompositionSectionComponentButton({
  compact = false,
  partCard,
}: {
  /**
   * Half-height layout for the two-row grid used when a character has more than three components:
   * a smaller glyph with the pinyin inline before the label, so each cell fits in half the row.
   */
  compact?: boolean;
  partCard: ComponentButtonMorphemeSlice;
}) {
  const { morphemeHrefObject } = useContext(MorphemeMnemonicsMeaningViewContext);
  const [catalogMnemonic, ownMnemonic] = useContextSelectors(
    GraphemeMorphemePanelsContext,
    (c) => c.readonlySlice.data.mnemonic?.trim() ?? "",
    (c) => c.userSlice.data.meaningCard?.description?.trim() ?? "",
  );
  const prefix = morphemeHrefObject;
  // A decomposition component navigates to that component's own page rather than opening the
  // preview sheet — the reader is drilling into the component, not glancing at it mid-sentence
  // the way a bracketed mnemonic link does.
  const cardPageNavigator = useMorphemePanelsHrefNavigator();
  const grapheme = partCard.graphemes?.[0]?.glyph ?? "";
  const selectionItems = useMemo(
    () => (grapheme === "" ? [] : [morphemeSelectionItem(grapheme)]),
    [grapheme],
  );
  const { consumePress, isSelected, onLongPress } = useAppSelection(selectionItems);
  const getGraphemeGlyph = useGetGraphemeGlyph();
  const graphemeGlyph = getGraphemeGlyph(grapheme);
  const partLabel =
    getMnemonicGraphemeLabel(ownMnemonic || catalogMnemonic, grapheme) ??
    partCard.componentMnemonic ??
    partCard.back ??
    partCard.pinyin.gloss ??
    "component";
  // A gloss may also appear in the alias list (日: sun, Japan). The actual gloss wins.
  const normalizedLabel = partLabel.trim().toLocaleLowerCase();
  const isMnemonicLabel =
    normalizedLabel === partCard.back?.trim().toLocaleLowerCase()
      ? partCard.isMeaningMnemonic === true
      : normalizedLabel !== partCard.pinyin.gloss?.trim().toLocaleLowerCase() &&
        [partCard.componentMnemonic, ...(partCard.componentMnemonicTerms ?? [])].some(
          (term) => term?.trim().toLocaleLowerCase() === normalizedLabel,
        );
  // Only surface pinyin for prevalent characters — stroke primitives like 丿 (piě) resolve to
  // an obscure prevalence and get no reading, matching the rest of the mnemonics views.
  const prevalentPinyin = usePrevalentPinyin(grapheme, partCard.pinyin.pinyin);
  const partPinyin =
    prevalentPinyin !== null && prevalentPinyin.length > 0
      ? toDiacriticPinyin(prevalentPinyin)
      : null;
  const svgStrokeList = useMemo(
    () =>
      partCard.graphemeStrokes === null
        ? null
        : partCard.graphemeStrokes.map(({ median, path }) => ({ median, path })),
    [partCard.graphemeStrokes],
  );

  return (
    <LongPressable
      accessibilityLabel={`${grapheme}, decomposition component`}
      accessibilityState={{ selected: isSelected }}
      className={cn(
        "flex-row items-center rounded-xs",
        compact ? "gap-1.5 px-1" : "gap-2 p-1",
        isSelected && "bg-primary/10",
      )}
      hitSlop={8}
      onLongPress={grapheme === "" ? undefined : onLongPress}
      onPress={() => {
        if (grapheme === "" || consumePress()) {
          return;
        }
        cardPageNavigator.push.mutate({ morpheme: grapheme, prefix });
      }}
    >
      <GraphemeSvgGlyph
        glyph={graphemeGlyph}
        showBackground={false}
        size={compact ? 18 : 32}
        strokeList={svgStrokeList}
      />
      {compact ? (
        <View className="min-w-0 flex-1 flex-row items-baseline gap-1">
          {partPinyin === null ? null : (
            <Text className="text-[10px] font-medium text-muted-foreground" numberOfLines={1}>
              {partPinyin}
            </Text>
          )}
          <Text
            className={cn(
              "min-w-0 shrink text-[11px] text-muted-foreground",
              isMnemonicLabel ? "font-normal italic" : "font-bold",
            )}
            numberOfLines={1}
          >
            {partLabel}
          </Text>
        </View>
      ) : (
        <View className="min-w-0 flex-1 flex-col justify-center">
          {partPinyin === null ? null : (
            <Text className="text-[10px] font-medium text-muted-foreground" numberOfLines={1}>
              {partPinyin}
            </Text>
          )}
          <Text
            className={cn(
              "text-[11px] text-muted-foreground",
              isMnemonicLabel ? "font-normal italic" : "font-bold",
            )}
            numberOfLines={1}
          >
            {partLabel}
          </Text>
        </View>
      )}
    </LongPressable>
  );
}
